import { SCENARIO_UNIT_BARRELS } from '@/utils/scenarioUnits';
import { Grid, TextField } from '@mui/material';
import React from 'react';

import {
  FIELD_ECONOMY_PARAM_ORDER,
  getEconomyAttributeHelperText,
  getEconomyAttributeLabel,
  getEconomyAttributePlaceholder,
  isRequiredForEconomicsCalculation,
  LAYER_ECONOMY_PARAM_ORDER,
  sortEconomyAttributes,
} from '@/utils/economyTemplateLabels';

export interface EconomyParamAttribute {
  id: string;
  name: string;
  type: string;
  value?: string | null;
}

interface EconomyTemplateParamsFormProps {
  attributes: EconomyParamAttribute[];
  templateType: 'field' | 'layer';
  unitId: number;
  values: Record<string, string>;
  onChange: (attributeId: string, value: string) => void;
  disabled?: boolean;
}

export const EconomyTemplateParamsForm: React.FC<EconomyTemplateParamsFormProps> = ({
  attributes,
  templateType,
  unitId,
  values,
  onChange,
  disabled = false,
}) => {
  const order = templateType === 'field' ? FIELD_ECONOMY_PARAM_ORDER : LAYER_ECONOMY_PARAM_ORDER;
  const sorted = sortEconomyAttributes(attributes, order);

  return (
    <Grid container spacing={2}>
      {sorted.map((attribute) => {
        const label = getEconomyAttributeLabel(attribute.name, unitId);
        const isRequired = isRequiredForEconomicsCalculation(attribute.name, templateType);
        const placeholder = getEconomyAttributePlaceholder(attribute.name, unitId);
        const helperText = getEconomyAttributeHelperText(attribute.name, unitId, templateType);
        const value = values[attribute.id] ?? attribute.value ?? '';

        return (
          <Grid item xs={12} sm={6} key={attribute.id}>
            <TextField
              fullWidth
              size="small"
              label={isRequired ? `${label} *` : label}
              placeholder={placeholder}
              helperText={helperText}
              value={value}
              onChange={(e) => onChange(attribute.id, e.target.value)}
              disabled={disabled}
              type={attribute.type === 'float' ? 'number' : 'text'}
              inputProps={attribute.type === 'float' ? { step: 'any', inputMode: 'decimal' } : undefined}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export function buildEmptyParamValues(attributes: EconomyParamAttribute[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const attr of attributes) {
    values[attr.id] = attr.value ?? '';
  }
  return values;
}

export function paramValuesFromAttributes(attributes: EconomyParamAttribute[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const attr of attributes) {
    values[attr.id] = attr.value ?? '';
  }
  return values;
}

/** Display discount as % in form when stored as decimal (0.1 → 10). */
export function formatStoredRateForForm(name: string, raw: string): string {
  if (!raw.trim()) return '';
  if (name !== 'discount_rate' && name !== 'royalty_rate' && name !== 'income_tax_rate') {
    return raw;
  }
  const num = Number(raw);
  if (!Number.isFinite(num)) return raw;
  if (num > 0 && num <= 1) {
    return String(num * 100);
  }
  return raw;
}

/** Persist rate fields as decimal fractions. */
export function formatFormRateForStorage(name: string, raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (name !== 'discount_rate' && name !== 'royalty_rate' && name !== 'income_tax_rate') {
    return trimmed;
  }
  const num = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(num)) return trimmed;
  if (num > 1) {
    return String(num / 100);
  }
  return trimmed;
}

export const RATE_FIELD_NAMES = new Set(['discount_rate', 'royalty_rate', 'income_tax_rate']);

export function isOptionalLayerParam(name: string): boolean {
  return (
    name === 'additional_cost_per_active_well' ||
    name === 'additional_cost_per_year' ||
    name === 'additional_cost_per_oil_unit'
  );
}

export function unitSuffix(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'bbl' : 't';
}
